import styled from '@emotion/styled';
import { Color, PrimaryButton } from '@liveoptics/react-ui';
import { useRequest } from 'ahooks';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { getOpportunities, registerNewUser, setOpportunity, verifyEndUser } from '../../../api/api';
import { Error, OpportunityMode, TheaterType, TheaterTypeIndexMapper } from '../../../enums';
import { VerifyOwnerState } from '../../../enums/VerifyOwnerState';
import {
  EmailCheckResponse,
  Opportunity,
  RegisterNewUserRequest,
  SalesforceAccount,
  SalesforceInfo,
  VerifyEndUserRequest,
} from '../../../interfaces';
import { BackButton } from '../../buttons';
import TextButton from '../../buttons/TextButton';
import { SalesforceAccountsAutoComplete } from '../../form-fields';
import { TheatersDropDownMenu } from '../../menus';
import { FormError } from '../form-error/FormError';
import { NoOpportunitiesFoundSubForm } from './NoOpportunitiesFoundSubForm';
import { OpportunitiesFoundSubForm } from './OpportunitiesFoundSubForm';
import { SelectionOption } from './SelectionOption';
import { SetOpportunityFormState } from './SetOpportunityFormState';

export interface SetOpportunityFormProps {
  projectId: number;
  defaultTheater: TheaterType;
  emailCheckResponse?: EmailCheckResponse;
  registerNewUserRequest?: RegisterNewUserRequest;
  defaultSalesforceInfo?: SalesforceInfo;
  verifyOwnerStateCallback?: Dispatch<SetStateAction<VerifyOwnerState>>;
  onCancel: () => void;
  onSuccess: (state: VerifyOwnerState, email?: string) => void;
}

const Form = styled.div`
  padding: 26px;
  > p:is(:first-of-type) {
    margin-top: 0;
  }
`;

const ModalActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: baseline;
  width: 100%;
  padding-top: 32px;
  a {
    margin-right: 24px;
  }
`;

const Title = styled.p`
  margin-top: 16px;
  margin-bottom: 0px;
  text-align: left;
  font: normal normal 600 16px/28px Open Sans;
  letter-spacing: 0px;
  color: ${Color.coolGray700};
  opacity: 1;
`;

const Remark = styled.p`
  margin: 0;
  text-align: left;
  font: normal normal normal 12px/20px Open Sans;
  letter-spacing: 0px;
  color: ${Color.lameGrey700};
  opacity: 1;
`;

const BackButtonContainer = styled.div`
  margin-right: auto;
`;

const Loading = styled.p`
  justify-self: center;
  font: normal normal normal 16px/24px Open Sans;
  color: ${Color.coolGray700};
`;

export const SetOpportunityForm = (props: SetOpportunityFormProps) => {
  const getInitialSfdcAccount = (
    registerNewUserRequest?: RegisterNewUserRequest,
    emailCheckResponse?: EmailCheckResponse,
    defaultSalesforceInfo?: SalesforceInfo
  ): SalesforceAccount | undefined => {
    if (emailCheckResponse?.SFAccountId && emailCheckResponse?.CompanyName)
      return {
        SFAccountID: emailCheckResponse.SFAccountId,
        Name: emailCheckResponse.CompanyName,
      };
    else if (registerNewUserRequest?.CompanySFAccountId && registerNewUserRequest?.CompanyName)
      return {
        SFAccountID: registerNewUserRequest.CompanySFAccountId,
        Name: registerNewUserRequest.CompanyName,
      };
    else if (
      defaultSalesforceInfo?.salesforceAccountId &&
      defaultSalesforceInfo?.salesforceAccountId !== 'NA' &&
      defaultSalesforceInfo?.salesforceAccountId !== '' &&
      defaultSalesforceInfo?.salesforceAccountId !== null &&
      defaultSalesforceInfo?.salesforceAccountName &&
      defaultSalesforceInfo?.salesforceAccountName !== '' &&
      defaultSalesforceInfo?.salesforceAccountName !== null
    )
      return {
        SFAccountID: defaultSalesforceInfo.salesforceAccountId,
        Name: defaultSalesforceInfo.salesforceAccountName,
      };
    else return undefined;
  };

  const getRequestOpportunityMode = (
    selectionOption: SelectionOption,
    opportunityId: number | undefined,
    opportunityMode: OpportunityMode
  ): OpportunityMode => {
    if (selectionOption === SelectionOption.Opportunities)
      if (opportunityId) return OpportunityMode.Assigned;
      else return OpportunityMode.None;
    else return opportunityMode;
  };

  const [theater, setTheater] = useState<TheaterType>(
    props.registerNewUserRequest?.Theater
      ? TheaterTypeIndexMapper[props.registerNewUserRequest?.Theater]
      : props.defaultTheater
  );
  const [sfdcAccount, setSfdcAccount] = useState<SalesforceAccount | undefined>(
    getInitialSfdcAccount(
      props.registerNewUserRequest,
      props.emailCheckResponse,
      props.defaultSalesforceInfo
    )
  );
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [opportunityId, setOpportunityId] = useState<number | undefined>(
    props.defaultSalesforceInfo?.opportunityId && props.defaultSalesforceInfo.opportunityId !== 0
      ? props.defaultSalesforceInfo.opportunityId
      : undefined
  );
  const [opportunityMode, setOpportunityMode] = useState<OpportunityMode>(
    !props.defaultSalesforceInfo?.opportunityId && props.defaultSalesforceInfo?.opportunityMode
      ? props.defaultSalesforceInfo.opportunityMode
      : OpportunityMode.None
  );
  const [selectionOption, setSelectionOption] = useState<SelectionOption>(
    (!props.defaultSalesforceInfo?.opportunityId ||
      props.defaultSalesforceInfo?.opportunityId === 0) &&
      props.defaultSalesforceInfo?.opportunityMode &&
      props.defaultSalesforceInfo.opportunityMode !== OpportunityMode.None &&
      props.defaultSalesforceInfo.opportunityMode !== OpportunityMode.Assigned
      ? SelectionOption.OpportunityMode
      : SelectionOption.Opportunities
  );
  const [formState, setFormState] = useState<SetOpportunityFormState>(
    props.emailCheckResponse?.SFAccountId ||
      props.registerNewUserRequest?.CompanySFAccountId ||
      props.defaultSalesforceInfo?.salesforceAccountId
      ? SetOpportunityFormState.OpportunitiesNotFound
      : SetOpportunityFormState.NoSalesForceAccount
  );
  const [errorMessage, setErrorMessage] = useState('');

  const opportunitiesRequest = useRequest(getOpportunities, {
    manual: true,
    debounceWait: 500,
  });

  const registerNewUserRequest = useRequest(registerNewUser, {
    manual: true,
    debounceWait: 500,
  });

  const verifyEndUserRequest = useRequest(verifyEndUser, {
    manual: true,
    debounceWait: 500,
  });

  const setOpportunityRequest = useRequest(setOpportunity, {
    manual: true,
    debounceWait: 500,
  });

  const handleTheaterChange = useCallback((value: string) => setTheater(TheaterType[value]), []);

  const handleSfdcAccountChange = useCallback(
    (sfdcAccount?: SalesforceAccount) => setSfdcAccount(sfdcAccount),
    []
  );

  const handleSelectionOptionChange = useCallback(
    (selectionOption: SelectionOption) => setSelectionOption(selectionOption),
    []
  );

  const handleOpportunityIdChange = useCallback(
    (opportunityId?: number) => setOpportunityId(opportunityId),
    []
  );

  const handleOpportunityModeChange = useCallback(
    (opportunityMode: OpportunityMode) => setOpportunityMode(opportunityMode),
    []
  );

  const handleVerifyModalStateChange = useCallback(
    (verifyOwnerState: VerifyOwnerState) => () =>
      props.verifyOwnerStateCallback?.(verifyOwnerState),
    [props]
  );

  const handleVerifyEndUserRequest = useCallback(() => {
    const verifyEndUserRequestDTO: VerifyEndUserRequest = {
      mode: getRequestOpportunityMode(selectionOption, opportunityId, opportunityMode),
      salesforceAccountId: sfdcAccount ? parseInt(sfdcAccount.SFAccountID) : undefined,
      opportunityId: selectionOption === SelectionOption.Opportunities ? opportunityId : undefined,
      ownerAccountId: 0,
    };
    if (props.registerNewUserRequest)
      registerNewUserRequest
        .runAsync(props.registerNewUserRequest)
        .then((data) => {
          verifyEndUserRequestDTO.ownerAccountId = data.Data.Id;
          verifyEndUserRequest
            .runAsync(props.projectId, verifyEndUserRequestDTO)
            .then(() => {
              props.onSuccess(
                VerifyOwnerState.VerifyEndUserAndSetOpportunity,
                props.registerNewUserRequest?.Email ?? props.emailCheckResponse?.Email
              );
            })
            .catch(() => {
              setErrorMessage(Error.VerifyEndUser);
            });
        })
        .catch(() => {
          setErrorMessage(Error.RegisterNewUser);
        });
    else {
      verifyEndUserRequestDTO.ownerAccountId = props.emailCheckResponse?.ExistingAccountId ?? 0;
      verifyEndUserRequest
        .runAsync(props.projectId, verifyEndUserRequestDTO)
        .then(() => {
          props.onSuccess(
            VerifyOwnerState.VerifyEndUserAndSetOpportunity,
            props.registerNewUserRequest?.Email ?? props.emailCheckResponse?.Email
          );
        })
        .catch(() => {
          setErrorMessage(Error.VerifyEndUser);
        });
    }
  }, [
    opportunityId,
    opportunityMode,
    props,
    registerNewUserRequest,
    selectionOption,
    sfdcAccount,
    verifyEndUserRequest,
  ]);

  const handleSetOpportunityRequest = useCallback(() => {
    setOpportunityRequest
      .runAsync(props.projectId, {
        mode: getRequestOpportunityMode(selectionOption, opportunityId, opportunityMode),
        opportunityId:
          selectionOption === SelectionOption.Opportunities ? opportunityId : undefined,
        salesforceAccountId: sfdcAccount ? parseInt(sfdcAccount.SFAccountID) : undefined,
      })
      .then(() => {
        props.onSuccess(VerifyOwnerState.SetOpportunity);
      })
      .catch(() => {
        setErrorMessage(Error.SetOpportunity);
      });
  }, [opportunityId, opportunityMode, props, selectionOption, setOpportunityRequest, sfdcAccount]);

  const handleSaveClick = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e?.preventDefault();
      setErrorMessage('');
      if (props.verifyOwnerStateCallback) handleVerifyEndUserRequest();
      else handleSetOpportunityRequest();
    },
    [handleSetOpportunityRequest, handleVerifyEndUserRequest, props.verifyOwnerStateCallback]
  );

  useEffect(() => {
    if (sfdcAccount?.SFAccountID) {
      setFormState(SetOpportunityFormState.Loading);
      if (sfdcAccount.SFAccountID !== props.defaultSalesforceInfo?.salesforceAccountId) {
        handleOpportunityIdChange();
        handleOpportunityModeChange(OpportunityMode.None);
      }
      opportunitiesRequest
        .runAsync(sfdcAccount?.SFAccountID)
        .then((data) => {
          if (data && data.length !== 0) {
            setOpportunities(data);
            setFormState(SetOpportunityFormState.OpportunitiesFound);
            if (sfdcAccount.SFAccountID !== props.defaultSalesforceInfo?.salesforceAccountId)
              handleSelectionOptionChange(SelectionOption.Opportunities);
          } else {
            setOpportunities([]);
            setFormState(SetOpportunityFormState.OpportunitiesNotFound);
            if (
              sfdcAccount.SFAccountID !== props.defaultSalesforceInfo?.salesforceAccountId ||
              (props.defaultSalesforceInfo && (!opportunityId || opportunityId == 0))
            )
              handleSelectionOptionChange(SelectionOption.OpportunityMode);
          }
        })
        .catch(() => {
          setErrorMessage(Error.General);
        });
    }
  }, [sfdcAccount]);

  const initialSfdcAccount: SalesforceAccount | undefined = getInitialSfdcAccount(
    props.registerNewUserRequest,
    props.emailCheckResponse,
    props.defaultSalesforceInfo
  );

  const getDefaultSalesforceAccountAutoCompleteString = (
    formState: SetOpportunityFormState,
    initialSfdcAccount?: SalesforceAccount
  ): string => {
    if (initialSfdcAccount && formState !== SetOpportunityFormState.NoSalesForceAccount)
      return `${initialSfdcAccount.Name} : ${initialSfdcAccount.SFAccountID}`;
    else return '';
  };

  const isOpportunityIdUndefinedWhileOpportunitiesSelection = (
    opportunityId: number | undefined,
    selectionOption: SelectionOption
  ): boolean => {
    return !opportunityId && selectionOption === SelectionOption.Opportunities;
  };

  const isModeNoneWhileModeSelection = (
    opportunityMode: OpportunityMode,
    selectionOption: SelectionOption
  ): boolean => {
    return (
      opportunityMode === OpportunityMode.None &&
      selectionOption === SelectionOption.OpportunityMode
    );
  };

  const renderOpportunitiesInput = () => {
    switch (formState) {
      case SetOpportunityFormState.OpportunitiesFound:
        return (
          <OpportunitiesFoundSubForm
            selectionOption={selectionOption}
            opportunities={opportunities}
            defaultOpportunity={opportunityId}
            defaultOpportunityMode={opportunityMode}
            onSelectionOptionChange={handleSelectionOptionChange}
            onOpportunityModeChange={handleOpportunityModeChange}
            onOpportunityIdChange={handleOpportunityIdChange}
          />
        );
      case SetOpportunityFormState.OpportunitiesNotFound:
        return (
          <NoOpportunitiesFoundSubForm
            defaultOpportunityMode={opportunityMode}
            onOpportunityModeChange={handleOpportunityModeChange}
          />
        );
      case SetOpportunityFormState.Loading:
        return <Loading>Loading...</Loading>;
    }
  };

  return (
    <Form>
      {errorMessage ? <FormError error={errorMessage} /> : <></>}
      <Title>Theater</Title>
      <TheatersDropDownMenu onChange={handleTheaterChange} defaultValue={TheaterType[theater]} />
      <Title>Account Name / SFDC ID</Title>
      <Remark>Use the default Salesforce.com account or select another account.</Remark>
      <SalesforceAccountsAutoComplete
        theater={theater}
        defaultValue={getDefaultSalesforceAccountAutoCompleteString(formState, initialSfdcAccount)}
        placeholder="Account Name / SFDC ID"
        onAccountChange={handleSfdcAccountChange}
      />
      {sfdcAccount ? <Title>Assign Opportunity ID To This Project</Title> : <></>}
      {sfdcAccount ? renderOpportunitiesInput() : <></>}
      <ModalActions>
        {props.verifyOwnerStateCallback ? (
          <BackButtonContainer>
            <BackButton
              onClick={handleVerifyModalStateChange(VerifyOwnerState.SelectOwner)}
            ></BackButton>
          </BackButtonContainer>
        ) : (
          <></>
        )}
        <TextButton onClick={props.onCancel}>Cancel</TextButton>
        <PrimaryButton
          disabled={
            isOpportunityIdUndefinedWhileOpportunitiesSelection(opportunityId, selectionOption) ||
            isModeNoneWhileModeSelection(opportunityMode, selectionOption) ||
            !sfdcAccount ||
            formState == SetOpportunityFormState.Loading
          }
          clickFn={handleSaveClick}
          loading={
            props.verifyOwnerStateCallback
              ? verifyEndUserRequest.loading || registerNewUserRequest.loading
              : setOpportunityRequest.loading
          }
          loadingText="Loading..."
        >
          Save
        </PrimaryButton>
      </ModalActions>
    </Form>
  );
};
